import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import HelpDrawer from '../help/HelpDrawer.jsx';
import InstallPrompt from '../components/InstallPrompt.jsx';
import DemoRibbon from '../components/DemoRibbon.jsx';
import QrScanner from '../components/QrScanner.jsx';
import { chargerQrProblem } from '../config/index.js';
import { landingRouteForRole } from '../pages/login/loginRoles.js';
import { APP_NAME, DEFAULT_SLUG, menuByPageId, canAccess, menuForRole, routeFor } from './menuData.js';

const PROFILE_SLUG = 'user-profile-vehicle-settings';
const MAX_BOTTOM_TABS = 4; // the fifth slot is always the Account tab
/**
 * Shared chrome for every ChargeOS layout: fixed top navbar, page outlet, and the
 * mobile bottom bar (<= 991px, hidden on desktop).
 *
 * Navigation is role-based: the links come from menuForRole(user.role), the same
 * access lists App.jsx uses to guard routes, so nobody is shown a page they cannot
 * open. Signed-out visitors see only public pages.
 *
 * Desktop: one-row top bar; the links are measured and those that do not fit the
 * available width collapse into a "More" menu, so the bar never wraps or overlaps.
 * Mobile: up to four role tabs plus Account in the bottom bar; any remaining pages
 * are listed behind the header's menu button. Profile and logout live on the Account tab.
 */
export default function AppShell({ chrome = true }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // Chrome-less pages (sign-in, register, errors) have no top bar, so the body must not reserve room for one.
  useEffect(() => {
    document.body.classList.toggle('chargeos-chromeless', !chrome);
    return () => document.body.classList.remove('chargeos-chromeless');
  }, [chrome]);

  const role = isAuthenticated ? user?.role : undefined;
  // The profile page is reached through the avatar (desktop) or the Account tab (mobile).
  const items = menuForRole(role).filter((m) => m.slug !== PROFILE_SLUG);
  // The site root shows the station locator, so its tab is the current one there too.
  const currentPath = location.pathname === '/' ? `/${DEFAULT_SLUG}` : location.pathname;
  const isCurrent = (item) => currentPath === `/${item.slug}`;

  // Mobile tabs: the role's landing page is always included, then pages in menu order.
  const landingRoute = isAuthenticated ? landingRouteForRole(role) : `/${DEFAULT_SLUG}`;
  // Signed-out visitors see the driver tabs: tapping one that needs an account leads to sign-in and back.
  const tabPool = role ? items : menuForRole('driver').filter((m) => m.slug !== PROFILE_SLUG);
  const preferred = [...tabPool].sort((a, b) => Number(routeFor(b) === landingRoute) - Number(routeFor(a) === landingRoute));
  const tabSet = new Set(preferred.slice(0, MAX_BOTTOM_TABS));
  const bottom = tabPool.filter((m) => tabSet.has(m));
  const beyondTabs = items.filter((m) => !tabSet.has(m) && !bottom.some((t) => t.slug === m.slug));

  // Desktop priority nav: a hidden copy of every link is measured, and only as many
  // links as fit the row are shown inline. Re-runs whenever the bar is resized.
  const linksRef = useRef(null);
  const measureRef = useRef(null);
  const [inlineCount, setInlineCount] = useState(items.length);
  const itemsKey = items.map((m) => m.pageId).join(',');
  useLayoutEffect(() => {
    const container = linksRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;
    const fit = () => {
      const available = container.clientWidth;
      let used = 0;
      let count = 0;
      for (const li of measure.children) {
        used += li.offsetWidth;
        if (used > available) break;
        count += 1;
      }
      setInlineCount(count);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [itemsKey]);

  // Scan-to-charge (phones): drivers go to the charging page with the scanned code; visitors without an
  // account go to the walk-up checkout the sticker itself points at. Staff-only roles do not get the button.
  const chargingPage = Object.values(menuByPageId).find((m) => m.slug === 'live-charging-telemetry');
  const canCharge = !role || (chargingPage && canAccess(chargingPage, role));
  const showScan = chrome && canCharge && currentPath !== '/live-charging-telemetry';
  const onScanned = (text) => {
    const problem = chargerQrProblem(text);
    if (problem) { setScanError('That is not a ChargeOS charger code. Scan the sticker next to the connector.'); return; }
    setScanning(false);
    setScanError('');
    const url = new URL(text.trim());
    navigate(role ? `/live-charging-telemetry?qr=${encodeURIComponent(text.trim())}` : `${url.pathname}${url.search}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const accountPaths = isAuthenticated ? [`/${PROFILE_SLUG}`] : ['/login', '/register', '/forgot-password', '/reset-password'];
  const accountActive = accountPaths.includes(location.pathname);

  const bottomNav = (
    <nav className="chargeos-mobile-bottom-nav" id="chargeos-mobile-bottom-nav" aria-label="Mobile Bottom Navigation">
      <div className="chargeos-bottom-nav-inner">
        {bottom.map((item) => (
          <button
            key={item.pageId}
            type="button"
            className={`chargeos-bottom-nav-tab${isCurrent(item) ? ' active' : ''}`}
            data-page-id={item.pageId}
            aria-label={item.name}
            aria-current={isCurrent(item) ? 'page' : undefined}
            title={item.name}
            onClick={() => navigate(routeFor(item))}
          >
            <span className="chargeos-tab-icon-wrap">
              <i className={item.icon} aria-hidden="true" />
            </span>
            <span className="chargeos-tab-label">{item.shortLabel}</span>
          </button>
        ))}
        <button
          type="button"
          className={`chargeos-bottom-nav-tab${accountActive ? ' active' : ''}`}
          id="bottom-nav-account"
          aria-label={isAuthenticated ? 'My account' : 'Sign in'}
          aria-current={accountActive ? 'page' : undefined}
          title={isAuthenticated ? 'My account' : 'Sign in'}
          onClick={() => navigate(isAuthenticated ? `/${PROFILE_SLUG}` : '/login')}
        >
          <span className="chargeos-tab-icon-wrap">
            <i className={isAuthenticated ? 'fas fa-user' : 'fas fa-sign-in-alt'} aria-hidden="true" />
          </span>
          <span className="chargeos-tab-label">{isAuthenticated ? 'Account' : 'Sign in'}</span>
        </button>
      </div>
    </nav>
  );

  if (!chrome) {
    return (
      <>
        <HelpDrawer floating />
        <main>
          <DemoRibbon />
          <Outlet />
        </main>
        {bottomNav}
        <InstallPrompt />
      </>
    );
  }

  const showMoreOnDesktop = inlineCount < items.length;
  const showMoreOnMobile = beyondTabs.length > 0;
  const showMore = showMoreOnDesktop || showMoreOnMobile;
  const moreToggleClass = `${showMoreOnMobile ? ' d-block' : ' d-none'}${showMoreOnDesktop ? ' d-lg-block' : ' d-lg-none'}`;
  const overflowActive = items.some((item, index) => index >= inlineCount && isCurrent(item));

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary text-light fixed-top chargeos-top-navbar">
        <div className="container-fluid">
          <Link className="navbar-brand d-flex align-items-center" to={landingRoute}>
            <i className="fas fa-bolt me-2" aria-hidden="true" />
            {APP_NAME}
          </Link>
          <div className="chargeos-top-links-wrap d-none d-lg-block" ref={linksRef}>
            <ul className="navbar-nav chargeos-top-links" id="navbarNav">
              {items.slice(0, inlineCount).map((item) => (
                <li className="nav-item" key={item.pageId}>
                  <NavLink
                    className={`nav-link${isCurrent(item) ? ' active' : ''}`}
                    to={routeFor(item)}
                    data-page-id={item.pageId}
                  >
                    {item.name}
                  </NavLink>
                </li>
              ))}
            </ul>
            <ul className="navbar-nav chargeos-top-links chargeos-top-links-measure" ref={measureRef} aria-hidden="true">
              {items.map((item) => (
                <li className="nav-item" key={item.pageId}>
                  <span className="nav-link">{item.name}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="ms-auto ms-lg-0 d-flex align-items-center chargeos-top-actions">
            {showMore && (
              <div className={`chargeos-more${moreToggleClass}`}>
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-light me-2 chargeos-more-toggle${overflowActive ? ' active' : ''}`}
                  id="nav-more-toggle"
                  aria-haspopup="true"
                  aria-expanded={moreOpen}
                  aria-controls="nav-more-menu"
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  <i className="fas fa-bars" aria-hidden="true" />
                  <span className="d-none d-lg-inline ms-2">More</span>
                  <span className="visually-hidden d-lg-none">All pages</span>
                </button>
                {moreOpen && (
                  <>
                    <button type="button" className="chargeos-more-backdrop" aria-label="Close menu" tabIndex={-1} onClick={() => setMoreOpen(false)} />
                    <ul className="chargeos-more-menu list-unstyled mb-0" id="nav-more-menu">
                      {items.map((item, index) => (
                        <li
                          key={item.pageId}
                          className={`chargeos-more-item${index < inlineCount ? ' chargeos-more-item-inline' : ''}${tabSet.has(item) ? ' chargeos-more-item-tabbed' : ''}`}
                        >
                          <NavLink className={`chargeos-more-link${isCurrent(item) ? ' active' : ''}`} to={routeFor(item)} data-page-id={item.pageId}>
                            <i className={`${item.icon} fa-fw me-2`} aria-hidden="true" />
                            {item.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
            <HelpDrawer />
            {isAuthenticated ? (
              <>
                <span className="me-3 user-greeting text-white d-none d-xxl-inline text-truncate">Hello, {user?.name || user?.email}!</span>
                <Link
                  to="/user-profile-vehicle-settings"
                  className="rounded-circle border border-white bg-light text-primary d-none d-lg-inline-flex align-items-center justify-content-center flex-shrink-0 me-2"
                  style={{ width: 32, height: 32 }}
                  aria-label="My profile and vehicles"
                  title="My profile and vehicles"
                  id="nav-profile-link"
                >
                  <i className="fas fa-user" aria-hidden="true" />
                </Link>
                <button type="button" className="btn btn-sm btn-outline-secondary d-none d-lg-inline-block text-nowrap" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-1" aria-hidden="true" />
                  Logout
                </button>
              </>
            ) : (
              <Link className="btn btn-sm btn-warning text-nowrap d-none d-lg-inline-block" to="/login" state={{ from: location }}>
                <i className="fas fa-sign-in-alt me-1" aria-hidden="true" />
                Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      <main>
        <DemoRibbon />
        {!online && <div className="chargeos-offline-bar" role="status"><i className="fas fa-wifi me-2" aria-hidden="true" />You are offline. Live availability and charging controls need a connection.</div>}
        <Outlet />
      </main>

      <footer className="pt-5 mt-5 d-none d-lg-block" />

      {showScan && (
        <button type="button" className="chargeos-scan-fab" id="btn-scan-fab" onClick={() => { setScanError(''); setScanning(true); }}>
          <i className="fas fa-qrcode" aria-hidden="true" />Scan
        </button>
      )}
      {scanning && <QrScanner onResult={onScanned} onClose={() => setScanning(false)} error={scanError} />}
      {bottomNav}
      <InstallPrompt />
    </>
  );
}
